/**
 * Human-readable string from RTK Query `unwrap()` rejections, Axios-style errors,
 * or Django REST bodies (`message`, `detail`, `error`, field errors).
 */

const MAX_MESSAGE = 280;

function looksBinary(text: string): boolean {
	if (text.startsWith('%PDF') || text.includes('\u0000')) return true;
	let bad = 0;
	const sample = text.slice(0, 200);
	for (let i = 0; i < sample.length; i += 1) {
		const code = sample.charCodeAt(i);
		if (code < 9 || (code > 13 && code < 32)) bad += 1;
	}
	return bad > 5;
}

function sanitizeMessage(text: string, fallback: string): string {
	if (!text || looksBinary(text)) return fallback;
	const trimmed = text.trim();
	if (!trimmed) return fallback;
	if (trimmed.length > MAX_MESSAGE) return `${trimmed.slice(0, MAX_MESSAGE)}…`;
	return trimmed;
}

export function getApiErrorMessage(
	err: unknown,
	fallback = 'Something went wrong. Please try again.'
): string {
	try {
		if (err == null) return fallback;

		const data =
			err && typeof err === 'object' && 'data' in err
				? (err as { data: unknown }).data
				: err;

		if (typeof data === 'string') {
			const pythonNonField = data.match(
				/non_field_errors['"]?\s*:\s*\[\s*['"](.+?)['"]\s*\]/s
			);
			if (pythonNonField?.[1]) {
				return pythonNonField[1];
			}
			const m = data.match(/^\s*\[\s*['"](.+?)['"]\s*\]\s*$/);
			return sanitizeMessage(m?.[1] ?? data, fallback);
		}

		if (data instanceof Blob) {
			return fallback;
		}

		// DRF `many=True` payloads return an array with one entry per item
		// (e.g. bulk cut/reserve). Collect the per-item messages, dedupe
		// identical ones (often the same error repeats for every row), and
		// summarize so the user sees a readable message instead of a raw JSON
		// dump.
		if (Array.isArray(data)) {
			const messages: string[] = [];
			for (const item of data) {
				if (item == null) continue;
				const msg = getApiErrorMessage(item, '');
				if (msg) messages.push(msg);
			}
			const unique = Array.from(new Set(messages));
			if (unique.length === 0) return fallback;
			if (unique.length === 1) {
				return sanitizeMessage(
					messages.length > 1
						? `${unique[0]} (${messages.length} items affected)`
						: unique[0],
					fallback
				);
			}
			const shown = unique.slice(0, 3).join(' ');
			return sanitizeMessage(
				unique.length > 3 ? `${shown} (+${unique.length - 3} more)` : shown,
				fallback
			);
		}

		if (data && typeof data === 'object') {
			const d = data as Record<string, unknown>;

			if (typeof d.message === 'string') {
				const m = d.message.match(/^\s*\[\s*['"](.+?)['"]\s*\]\s*$/);
				return sanitizeMessage(m?.[1] ?? d.message, fallback);
			}
			if (Array.isArray(d.message)) {
				return sanitizeMessage(d.message.map(String).join(' '), fallback);
			}

			if (typeof d.detail === 'string') {
				return sanitizeMessage(d.detail, fallback);
			}
			if (Array.isArray(d.detail)) {
				return sanitizeMessage(d.detail.map(String).join(' '), fallback);
			}

			if (typeof d.error === 'string') {
				return sanitizeMessage(d.error, fallback);
			}
			if (Array.isArray(d.error)) {
				return sanitizeMessage(d.error.map(String).join(' '), fallback);
			}

			if (Array.isArray(d.non_field_errors)) {
				return sanitizeMessage(d.non_field_errors.map(String).join(' '), fallback);
			}

			const parts: string[] = [];
			for (const [k, v] of Object.entries(d)) {
				if (k === 'message' || k === 'detail' || k === 'error') continue;
				if (Array.isArray(v)) parts.push(`${k}: ${v.map(String).join(', ')}`);
				else if (typeof v === 'string') parts.push(`${k}: ${v}`);
			}
			if (parts.length) return sanitizeMessage(parts.join(' | '), fallback);
		}

		return fallback;
	} catch {
		return fallback;
	}
}
